import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { UploadCloud, Loader2, CheckCircle2, XCircle, FileText } from "lucide-react";

const ACCEPT = ".txt,.md,.pdf,.docx";

// File Upload -> Training Data (specs/dataforge-file-upload-spec.md): uploads
// become synthetic "pages" in an existing crawl or a new lightweight "upload"
// crawl container, so the AI Quality Filter, Dataset Studio, and Export all
// work on them exactly like crawled pages -- no parallel system.
export default function UploadFiles({ crawls, onUploaded }) {
  const [files, setFiles] = useState([]);
  const [target, setTarget] = useState("new"); // "new" | a crawl id
  const [newCrawlName, setNewCrawlName] = useState("");
  const [runQualityCheck, setRunQualityCheck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const inputRef = useRef(null);

  const pickFiles = (e) => {
    setFiles(Array.from(e.target.files || []));
    setResults(null);
  };

  const upload = async () => {
    if (files.length === 0) { toast.error("Choose one or more files first"); return; }
    setBusy(true);
    setResults(null);
    try {
      const r = await api.uploadFiles(files, {
        crawlId: target === "new" ? undefined : target,
        crawlName: target === "new" ? newCrawlName.trim() : undefined,
        runQualityCheck,
      });
      setResults(r);
      if (r.inserted > 0) {
        toast.success(`${r.inserted} file(s) added to "${r.crawl.name}"`);
      }
      if (r.failed > 0) {
        toast.error(`${r.failed} file(s) failed -- see details below`);
      }
      onUploaded?.(r.crawl);
      setFiles([]);
      if (inputRef.current) inputRef.current.value = "";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="upload-files-panel" className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3.5 space-y-3">
      <div className="flex items-center gap-2">
        <UploadCloud className="h-3.5 w-3.5 text-emerald-400" />
        <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Upload Files</span>
        <span className="ml-auto font-mono text-[10px] text-slate-600">.txt .md .pdf .docx -- your own docs as training data</span>
      </div>

      <input
        ref={inputRef}
        data-testid="upload-files-input"
        type="file"
        multiple
        accept={ACCEPT}
        onChange={pickFiles}
        disabled={busy}
        className="block w-full text-xs font-mono text-slate-400 file:mr-3 file:rounded file:border file:border-border
                   file:bg-[#0B0F18] file:px-3 file:py-1.5 file:text-xs file:font-mono file:text-slate-300
                   hover:file:bg-slate-800 file:cursor-pointer cursor-pointer"
      />

      {files.length > 0 && (
        <ul data-testid="upload-files-selected" className="space-y-1 max-h-24 overflow-y-auto">
          {files.map((f, i) => (
            <li key={i} className="flex items-center gap-1.5 font-mono text-[11px] text-slate-400">
              <FileText className="h-3 w-3 shrink-0 text-slate-600" />
              <span className="truncate">{f.name}</span>
              <span className="ml-auto text-slate-600 shrink-0">{(f.size / 1024).toFixed(0)} KB</span>
            </li>
          ))}
        </ul>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs text-slate-400">Add to</Label>
          <Select value={target} onValueChange={setTarget}>
            <SelectTrigger data-testid="upload-target-select" className="bg-[#0B0F18] border-border text-sm h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="new">New upload crawl</SelectItem>
              {crawls.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {target === "new" && (
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400">New crawl name</Label>
            <Input data-testid="upload-new-crawl-name-input" value={newCrawlName}
              onChange={(e) => setNewCrawlName(e.target.value)}
              placeholder={`Uploads ${new Date().toISOString().slice(0, 10)}`}
              className="bg-[#0B0F18] border-border font-mono text-sm h-9" />
          </div>
        )}
      </div>

      <div className="flex items-center justify-between rounded-md border border-border bg-[#0B0F18] px-3 py-2">
        <Label className="text-xs text-slate-400">Score with AI Quality Filter on upload</Label>
        <Switch data-testid="upload-quality-check-switch" checked={runQualityCheck}
          onCheckedChange={setRunQualityCheck} />
      </div>

      <Button
        data-testid="upload-files-button"
        onClick={upload}
        disabled={busy || files.length === 0}
        className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold disabled:opacity-40"
      >
        {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <UploadCloud className="h-4 w-4 mr-1.5" />}
        {busy ? "Processing..." : `Upload ${files.length || ""} File${files.length === 1 ? "" : "s"}`}
      </Button>

      {results && (
        <div data-testid="upload-files-results" className="rounded border border-border bg-[#04060A] p-2.5 space-y-1 max-h-40 overflow-y-auto">
          {results.results.map((r, i) => (
            <div key={i} className="flex items-center gap-1.5 font-mono text-[11px]">
              {r.success
                ? <CheckCircle2 className="h-3 w-3 shrink-0 text-emerald-400" />
                : <XCircle className="h-3 w-3 shrink-0 text-rose-400" />}
              <span className={cn("truncate", r.success ? "text-slate-300" : "text-rose-300")}>{r.filename}</span>
              <span className="ml-auto shrink-0 text-slate-500">
                {r.success
                  ? `${r.content_length.toLocaleString()} chars${r.quality_score != null ? ` -- quality ${r.quality_score.toFixed(2)}` : ""}`
                  : r.error}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
